import { Nullable } from 'utils/types';

export enum FIELDS {
  EMPLOYED_TOTAL = 'currentIncomeEmployedTotal',
  SELF_EMPLOYED_TOTAL = 'currentIncomeSelfEmployedTotal',
  OTHER_TOTAL = 'currentIncomeOtherTotal',
  CURRENT_INCOME_PENSION = 'currentIncomePensionIncome',
  TOTAL = 'currentIncomeTotal',
  BASIC_GROSS_ANNUAL_SALARY = 'currentIncomeBasicGrossAnnualSalary',
  ANNUAL_BONUS = 'currentIncomeAnnualBonus',
  HAS_ANNUAL_BONUS_GUARANTEED = 'currentIncomeHasAnnualBonusGuaranteed',
  ANNUAL_COMMISSION = 'currentIncomeAnnualCommission',
  HAS_COMMISSION_GUARANTEED = 'currentIncomeHasCommissionGuaranteed',
  ANNUAL_OVERTIME = 'currentIncomeAnnualOvertime',
  HAS_OVERTIME_GUARANTEED = 'currentIncomeHasOvertimeGuaranteed',
  ANNUAL_SHIFT_ALLOWANCE = 'currentIncomeAnnualShiftAllowance',
  HAS_SHIFT_ALLOWANCE_GUARANTEED = 'currentIncomeHasShiftAllowanceGuaranteed',
  BUSINESS_TYPE = 'businessType',
  PERCENTAGE_OF_BUSINESS_OWNED = 'currentIncomePercentageOfBusinessOwned',
  LIMITED_COMPANY_SALARY = 'currentIncomeLimitedCompanySalary',
  LIMITED_COMPANY_DIVIDENDS = 'currentIncomeLimitedCompanyDividends',
  NET_PROFIT = 'currentIncomeNetProfit',
  BUSINESS_TRADING_DATE = 'currentIncomeBusinessTradingDate',

  INVESTMENT_PREFIX = 'currentIncomeInvestmentIncomes',
  INVESTMENT_PORTFOLIO_VALUE = 'investmentPortfolioValue',
  INVESTMENT_ANNUAL_INVESTMENT_INCOME = 'annualInvestmentIncome',
  INVESTMENT_ANNUAL_DIVIDEND_INCOME = 'annualDividendIncome',

  RENTAL_PREFIX = 'currentIncomeRentalIncomes',
  RENTAL_GROSS_ANNUAL_RENTAL_INCOME = 'grossAnnualRentalIncome',
  RENTAL_NO_OF_BTLS_IN_PORTFOLIO = 'noOfBtlsInPortfolio',
  RENTAL_MORTGAGE_PAYMENT = 'mortgagePayment',

  FOSTERING_PREFIX = 'currentIncomeFosteringIncomes',
  FOSTERING_GROSS_ANNUAL_FOSTERING_INCOME = 'grossAnnualFosteringIncome',

  MAINTENANCE_PREFIX = 'currentIncomeMaintenanceIncomes',
  MAINTENANCE_GROSS_ANNUAL_MAINTENANCE_INCOME = 'grossAnnualMaintenanceIncome',
  HAS_MAINTENANCE_GUARANTEED_OTHER_INCOME = 'hasMaintenanceGuaranteedOtherIncome',

  BENEFIT_PREFIX = 'currentIncomeBenefitIncomes',
  BENEFIT_GROSS_ANNUAL_BENEFIT_INCOME = 'grossAnnualBenefitIncome',
  HAS_BENEFIT_GUARANTEED_OTHER_INCOME = 'hasBenefitGuaranteedOtherIncome',

  OTHER_PREFIX = 'currentIncomeOtherIncomes',
  OTHER_GROSS_ANNUAL_OTHER_INCOME = 'grossAnnualOtherIncome',
  OTHER_HAS_BONUS_GUARANTEED_OTHER_INCOME = 'hasBonusGuaranteedOtherIncome',
}

export type Current = TotalIncomeSummary & {
  [FIELDS.BASIC_GROSS_ANNUAL_SALARY]: Nullable<string>;
  [FIELDS.ANNUAL_BONUS]: Nullable<string>;
  [FIELDS.HAS_ANNUAL_BONUS_GUARANTEED]: Nullable<boolean>;
  [FIELDS.ANNUAL_COMMISSION]: Nullable<string>;
  [FIELDS.HAS_COMMISSION_GUARANTEED]: Nullable<boolean>;
  [FIELDS.ANNUAL_OVERTIME]: Nullable<string>;
  [FIELDS.HAS_OVERTIME_GUARANTEED]: Nullable<boolean>;
  [FIELDS.ANNUAL_SHIFT_ALLOWANCE]: Nullable<string>;
  [FIELDS.HAS_SHIFT_ALLOWANCE_GUARANTEED]: Nullable<boolean>;
  [FIELDS.BUSINESS_TYPE]: Nullable<string>;
  [FIELDS.PERCENTAGE_OF_BUSINESS_OWNED]: Nullable<string>;
  [FIELDS.LIMITED_COMPANY_SALARY]: Nullable<string>;
  [FIELDS.LIMITED_COMPANY_DIVIDENDS]: Nullable<string>;
  [FIELDS.NET_PROFIT]: Nullable<string>;
  [FIELDS.BUSINESS_TRADING_DATE]: Nullable<string>;
  [FIELDS.INVESTMENT_PREFIX]: InvestmentIncome[];
  [FIELDS.RENTAL_PREFIX]: RentalIncome[];
  [FIELDS.FOSTERING_PREFIX]: FosteringIncome[];
  [FIELDS.MAINTENANCE_PREFIX]: MaintenanceIncome[];
  [FIELDS.BENEFIT_PREFIX]: BenefitIncome[];
  [FIELDS.OTHER_PREFIX]: OtherIncome[];
};

export type TotalIncomeSummary = {
  [FIELDS.EMPLOYED_TOTAL]: Nullable<number>;
  [FIELDS.SELF_EMPLOYED_TOTAL]: Nullable<number>;
  [FIELDS.OTHER_TOTAL]: Nullable<number>;
  [FIELDS.TOTAL]: Nullable<number>;
  [FIELDS.CURRENT_INCOME_PENSION]: Nullable<number>;
};

export type InvestmentIncome = {
  id: number;
  [FIELDS.INVESTMENT_PORTFOLIO_VALUE]: Nullable<string>;
  [FIELDS.INVESTMENT_ANNUAL_INVESTMENT_INCOME]: Nullable<string>;
  [FIELDS.INVESTMENT_ANNUAL_DIVIDEND_INCOME]: Nullable<string>;
};

export type RentalIncome = {
  id: number;
  [FIELDS.RENTAL_GROSS_ANNUAL_RENTAL_INCOME]: Nullable<string>;
  [FIELDS.RENTAL_NO_OF_BTLS_IN_PORTFOLIO]: Nullable<string>;
  [FIELDS.RENTAL_MORTGAGE_PAYMENT]: Nullable<string>;
};

export type FosteringIncome = {
  id: number;
  [FIELDS.FOSTERING_GROSS_ANNUAL_FOSTERING_INCOME]: Nullable<string>;
};

export type MaintenanceIncome = {
  id: number;
  [FIELDS.MAINTENANCE_GROSS_ANNUAL_MAINTENANCE_INCOME]: Nullable<string>;
  [FIELDS.HAS_MAINTENANCE_GUARANTEED_OTHER_INCOME]: Nullable<boolean>;
};

export type BenefitIncome = {
  id: number;
  [FIELDS.BENEFIT_GROSS_ANNUAL_BENEFIT_INCOME]: Nullable<string>;
  [FIELDS.HAS_BENEFIT_GUARANTEED_OTHER_INCOME]: Nullable<boolean>;
};

export type OtherIncome = {
  id: number;
  [FIELDS.OTHER_GROSS_ANNUAL_OTHER_INCOME]: Nullable<string>;
  [FIELDS.OTHER_HAS_BONUS_GUARANTEED_OTHER_INCOME]: Nullable<boolean>;
};

export enum BUSINESS_TYPES {
  LIMITED_COMPANY = 'LIMITED_COMPANY',
  PARTNERSHIP = 'PARTNERSHIP',
  SOLE_TRADER = 'SOLE_TRADER',
}
